import { Box, Button, Grid } from '@mui/material';
import _ from 'lodash';
import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import DialogComponent from '../../component/dialog/dialog.component';
import CommonInput from '../../component/form/common-input.component';
import i18n from '../../i18n';
import { Condiments, TaxGroup, ItemUnit, ITEM_TYPE } from '../../models';
import { ALLERGEN, INGREDIENTS } from '../../static/images/dish-base-data';
import { Option } from '../../component/form/select-input-multiple'


export type SetState<S extends Record<string, any>> = <K extends keyof S>(
    state: Pick<S, K> | null | ((prevState: Readonly<S>) => Pick<S, K> | S | null),
) => void;

export const useSetState = <S extends Record<string, any>>(
    initialState: S | (() => S),
): [S, SetState<S>] => {
    const [state, setState] = useState<S>(initialState);
    const setMergeState = useCallback((arg: any) => {
        setState((prevState) => {
            const newState = typeof arg === 'function' ? arg(prevState) : arg;
            return newState ? { ...prevState, ...newState } : prevState;
        });
    }, []);
    return [state, setMergeState];
};

function MuliSettingDishDialog({
    taxGroup,
    printGroup,
    majorGroup,
    mergetypes,
    condimentsGroup,
    onMultiDialogSetConfirm,
}: Props, ref: any) {

    const NO_CHANGE = '- No Change -';
    const initData = { 'allergen': ['-2'], 'other_detail_info': ['-2'] };

    const [state, setState] = useSetState<State>({
        multiSettingParams: initData,
        isShowMultiSettingDialog: false,
        avatarUrl: '',
        avatarFile: undefined,
    })


    useImperativeHandle(ref, () => ({
        _onSetDialogFunc
    }));

    const {
        multiSettingParams,
        isShowMultiSettingDialog,
        avatarFile,
    } = state;

    const _onSetDialogFunc = (flag: boolean) => setState({ isShowMultiSettingDialog: flag, multiSettingParams: initData, avatarFile: undefined, avatarUrl: '', });

    // 税率组
    const _taxGroup = taxGroup.map((item) => ({
        label: item.tax_group_name || '',
        value: item.tax_group_id || 0
    }));
    // 打印组
    const _printGroup = printGroup.map((item) => ({
        label: item.print_class_name || '',
        value: item.print_class_id || 0
    }))
    // 自定义报表主类型列表
    const _majorGroup = majorGroup.map((item) => ({
        label: item.major_group_name || '',
        value: item.major_group_id || 0
    }));
    // 自定义报表子类型列表
    const _mergetypes = mergetypes.map((item) => ({
        label: item.family_group_name || '',
        value: item.family_group_id || 0
    }));
    // 可选调味品组
    const _condimentsGroup = condimentsGroup.map((item) => ({
        label: item.comdiment_groups_name || '',
        value: item.scomdiment_groups_id || -1
    }));
    // 表单
    const formArr1 = [
        {
            key: 'item_type',
            label: i18n.t('menu_and_dishes_page_dish_type'),
            type: 'select',
            width: 4 as const,
            select: true,
            items: [
                { value: -2, label: NO_CHANGE },
                ...ITEM_TYPE
            ],
        },
        {
            key: 'tax_group',
            label: i18n.t('tax_group_page_label'),
            width: 4 as const,
            type: 'select',
            select: true,
            items: [
                { value: -2, label: NO_CHANGE },
                { value: -1, label: i18n.t('menu_and_dishes_page_not_specified') },
                ..._taxGroup
            ],
            allowClear: true,
        },
        {
            key: 'print_class',
            label: i18n.t('menu_and_dishes_page_print_class'),
            width: 4 as const,
            type: 'select',
            select: true,
            items: [
                { value: -2, label: NO_CHANGE },
                ..._printGroup
            ],
            allowClear: true,
        },
        {
            key: 'major_group',
            label: i18n.t('condiment_page_custom_form_main_type'),
            width: 4 as const,
            type: 'select',
            select: true,
            items: [
                { value: -2, label: NO_CHANGE },
                ..._majorGroup
            ],
            allowClear: true,
        },
        {
            key: 'family_group',
            label: i18n.t('condiment_page_custom_form_sub_type'),
            width: 4 as const,
            type: 'select',
            select: true,
            items: [
                { value: -2, label: NO_CHANGE },
                ..._mergetypes
            ],
            allowClear: true,
        },
        {
            key: 'class_id',
            label: i18n.t('menu_and_dishes_page_menu_dish_has_discount'),
            width: 4 as const,
            type: 'select',
            select: true,
            items: [
                { label: NO_CHANGE, value: -2 },
                { label: i18n.t('menu_and_dishes_page_has_discount'), value: 6 },
                { label: i18n.t('menu_and_dishes_page_has_not_discount'), value: 3 }
            ],
            allowClear: true,
        },
        {
            key: 'special_attributes',
            label: i18n.t('menu_and_dishes_page_special_attribute'),
            width: 4 as const,
            type: 'select',
            select: true,
            items: [
                { label: NO_CHANGE, value: -2 },
                { label: i18n.t('print_setting_page_none0'), value: 0 },
                { label: i18n.t('menu_and_dishes_page_chef_recommendation'), value: 1 },
                { label: i18n.t('menu_and_dishes_page_new_menu_recommendation'), value: 2 },
                { label: i18n.t('menu_and_dishes_page_has_discount'), value: 3 }
            ],
        },
        {
            key: 'required_condiment',
            label: i18n.t('condiment_page_group_optional'),
            width: 4 as const,
            type: 'select',
            select: true,
            items: [
                { value: -2, label: NO_CHANGE },
                ..._condimentsGroup
            ],
        },
    ]
    // 表单
    const formArr2 = [
        {
            key: 'item_description',
            label: i18n.t('google_text_dish_remarks'),
            width: 12 as const,
        },
        {
            key: 'ext_dish_info',
            label: i18n.t('menu_and_dishes_page_other_dish_mark'),
            width: 12 as const,
        },
        {
            key: 'allergen',
            label: i18n.t('menu_and_dishes_page_allergen'),
            width: 12 as const,
            inputType: 'SELECT_MUL' as const,
            options: [
                { value: '-2', label: NO_CHANGE },
                ...ALLERGEN.map((item) => ({
                    value: item.key,
                    label: i18n.t(item.text),
                    icon: <img style={{ width: 20, height: 20 }} alt={item.key} src={item.icon} />
                }))
            ]
        },
        {
            key: 'other_detail_info',
            label: i18n.t('menu_and_dishes_page_other_dish_pin'),
            width: 12 as const,
            inputType: 'SELECT_MUL' as const,
            options: [
                { value: '-2', label: NO_CHANGE },
                ...INGREDIENTS.map((item) => ({
                    value: item.key,
                    label: i18n.t(item.text),
                    icon: <img style={{ width: 20, height: 20 }} alt={item.key} src={item.icon} />
                }))
            ]
        }
    ]


    /**
     * 批量设置 -  onChange
     */
    const onMultiChange = (val: string | number | string[], key: string) => {
        const _multiSettingParams = _.cloneDeep(multiSettingParams);
        // multi select
        if (['allergen', 'other_detail_info'].includes(key) && Array.isArray(val) && val.length >= 1) {
            if (Number(val[0]) === -2 && val.length > 1) {
                val = val.slice(1);
            }
            if (Number(val[val.length - 1]) === -2) {
                val = val.slice(val.length - 1);
            }
        }
        // input
        if (typeof (val) === 'string' && val.indexOf(NO_CHANGE) > -1 && val !== NO_CHANGE) {
            val = val.replace(NO_CHANGE, '');
        }
        _.set(_multiSettingParams, key, val);
        setState({
            multiSettingParams: _multiSettingParams
        })
    }

    const onMultiSetConfirm = () => {
        setState({
            isShowMultiSettingDialog: false,
        })
        // 过滤输入框中value值为 NO_CHANGE 的键值对
        for (const key in multiSettingParams) {
            if (
                ['item_description', 'ext_dish_info', 'allergen', 'other_detail_info'].includes(key)
                && (multiSettingParams[key] === NO_CHANGE || (multiSettingParams[key]?.[0] === '-2'))
            ) {
                delete multiSettingParams[key]
            }
            else {
                continue
            }
        }
        onMultiDialogSetConfirm(multiSettingParams, avatarFile);
    }

    return (
        <DialogComponent
            className='second-menu-mul-setting-dish'
            defaultHeaderProps={{
                title: `${i18n.t('menu_and_dishes_page_multi_setting_dishes')}`,
                onClose: () => {
                    _onSetDialogFunc(false);
                }
            }}
            open={isShowMultiSettingDialog}
            fullWidth={true}
            scroll={'paper'}
            onClose={() => {
                _onSetDialogFunc(false);
            }}
            footer={
                <div>
                    <Button
                        color='primary'
                        variant='outlined'
                        onClick={() => {
                            _onSetDialogFunc(false);
                        }}
                    >{i18n.t('google_text_public_button_cancel')}</Button>
                    <Box marginLeft={4} display='inline-block'>
                        <Button
                            color='primary'
                            variant='contained'
                            onClick={onMultiSetConfirm}
                        >{i18n.t('google_text_confirm')}</Button>
                    </Box>
                </div>}
        >
            <Grid container spacing={2} style={{ marginTop: 0 }}>
                {/* <Grid item>
                    <AvatarCard
                        avatarUrl={avatarUrl}
                        onChangeAvatarUrl={(url, file) => {
                            setState({
                                avatarUrl: url,
                                avatarFile: file
                            })
                        }}
                        onDelete={() => {
                            setState({
                                avatarUrl: '',
                                avatarFile: undefined
                            })
                        }}
                    />
                </Grid> */}
                <Grid container item xs={12} spacing={4}>
                    {
                        formArr1.map((item) => {
                            const value = _.get(multiSettingParams, item.key);
                            return (
                                <Grid key={item.key} item xs={12} md={item.width}>
                                    <CommonInput
                                        type={item.type}
                                        select={item.select}
                                        items={item.items}
                                        variant={'outlined'}
                                        // defaultValue={item.select ? -2 : NO_CHANGE as any}
                                        SelectProps={{ displayEmpty: true }}
                                        fullWidth
                                        label={item.label}
                                        // value={_.get(multiSettingParams, item.key, undefined) as any}
                                        value={(value || typeof (value) === 'string') || typeof (value) === 'number' ? value : -2}
                                        onChange={(event: any) => {
                                            const val = event.target.value;
                                            onMultiChange(val, item.key);
                                        }}
                                    // allowClear={item.allowClear}
                                    />
                                </Grid>
                            )
                        })
                    }
                    {
                        formArr2.map((item) => {
                            const selectValue: any[] = [];
                            if (item.inputType === 'SELECT_MUL') {
                                const v: string[] = _.get(multiSettingParams, item.key, []);
                                item.options.forEach((iitem) => {
                                    if (iitem.value && v?.includes(`${iitem.value}`)) {
                                        selectValue.push(iitem);
                                    }
                                })
                            }
                            const onChange = (value) => {
                                if (item.inputType === 'SELECT_MUL') {
                                    const newValue = value.map((item: any) => (item.value))
                                    onMultiChange(newValue, item.key)
                                }
                                else {
                                    onMultiChange(value.target.value, item.key)
                                }
                            }
                            return (
                                <Grid item xs={12} md={item.width} key={item.key}>
                                    <CommonInput
                                        inputType={item.inputType}
                                        variant={'outlined'}
                                        fullWidth
                                        label={item.label}
                                        value={item.inputType === 'SELECT_MUL' ? selectValue : _.get(multiSettingParams, item.key, NO_CHANGE)}
                                        multipleProps={{
                                            options: item.options! as Option[],
                                            onChange,
                                        }}
                                        onChange={onChange}
                                        allowClear={true}
                                    />
                                </Grid>
                            )
                        })
                    }
                </Grid>
                {/* <Grid item>
                    <SwitchComponent
                        value={_.get(multiSettingParams, 'rule_lnr_limit_enable', false) as any}
                        label={i18n.t('menu_and_dishes_page_continue_to_add')}
                        onChange={(e, checked) => {
                            onMultiChange(Number(checked), 'rule_lnr_limit_enable')
                        }}
                    />
                </Grid>
                <Grid item>
                    <SwitchComponent
                        value={_.get(multiSettingParams, 'rules_ignore_the_display', false) as any}
                        label={i18n.t('menu_and_dishes_page_dish_edition_ignore_turn_rule')}
                        onChange={(e, checked) => {
                            onMultiChange(Number(checked), 'rules_ignore_the_display')
                        }}
                    />
                </Grid> */}
            </Grid>
        </DialogComponent >
    )
}

export default forwardRef(MuliSettingDishDialog)

interface Props {
    // 税率组
    taxGroup: TaxGroup[];
    // 调味品组
    condimentsGroup: Condiments[];
    // 打印组(岗位)
    printGroup: any[];
    // 报表主类别
    majorGroup: any[];
    // 报表子类别
    mergetypes: any[];
    // 菜品规格列表
    priceSpecList: ItemUnit[];
    onMultiDialogSetConfirm: (settingParams: { [key: string]: any }, avatarFile?: File) => void;
}

type State = {
    multiSettingParams: { [key: string]: any };
    isShowMultiSettingDialog: boolean;
    avatarUrl: string;
    avatarFile?: File;
}